import { useCallback, useRef } from 'react';

import { authStatus, useAuthContext } from '@picsart/growth-rc/AuthContext';
import AnalyticsService, { AnalyticsEnum } from '@picsart/rc/services/analytics';
import useMount from '@picsart/rc/useMount';

import { AnalyticEventTypes, LandingCategory } from 'constants/enums';
import { analyticModule, GTM_EXPERIMENT_URLS } from 'constants/main';

import usePageSlug from 'hooks/usePageSlug';
import type {
  ExperimentEvent,
  FireEvent,
  PageOpen,
  PageView,
  PushDataLayer,
  SendEvent,
  UseAnalytics,
} from './types';

let isInitialized = false;
let pageOpenFired = false;

const useAnalytics: UseAnalytics = (gtmDelay = 10000) => {
  const queueRef = useRef([]);
  const { status } = useAuthContext();
  const { slug: pagePath } = usePageSlug();

  const init = () => {
    const delay = GTM_EXPERIMENT_URLS.includes(pagePath) ? null : gtmDelay;
    AnalyticsService.init(
      process.env.REACT_APP_PUBLIC_GTM_ID,
      'landings',
      'dataLayer',
      AnalyticsEnum.gtag,
      delay,
    );

    isInitialized = true;
    queueRef.current.forEach(event => event());
    queueRef.current = [];
  };

  useMount(() => {
    if (!isInitialized && process.env.NODE_ENV !== ('development' as string)) {
      init();
    }
  });

  const sendEvent: SendEvent = useCallback(
    (eventAction, data) => AnalyticsService.sendEvent(eventAction, data),
    [],
  );

  const pushDataLayer: PushDataLayer = useCallback(
    dataLayer => AnalyticsService.pushDataLayer(dataLayer),
    [],
  );

  const sendEventNonInteraction: SendEvent = useCallback(
    (eventAction: string, data?: any) =>
      AnalyticsService.sendEventNonInteraction(eventAction, data),
    [],
  );

  const pageView: PageView = useCallback(page => AnalyticsService.pageView(page), []);

  const fireEvent: FireEvent = useCallback(
    (eventName, params, interaction = true) => {
      const send = interaction ? sendEvent : sendEventNonInteraction;
      if (isInitialized) {
        send(eventName, params);
      } else {
        queueRef.current.push(() => send(eventName, params));
      }
    },
    [sendEvent, sendEventNonInteraction],
  );

  const firePageOpen: PageOpen = useCallback(
    (screen: string, category: LandingCategory = LandingCategory.general, data?: any) => {
      if (!pageOpenFired) {
        fireEvent(
          AnalyticEventTypes.page_open,
          {
            module: analyticModule,
            category,
            is_logged: status === authStatus.resolved,
            screen_name: screen,
            appsFlyer: true,
            source: true,
            ...data,
          },
          false,
        );

        if (category === LandingCategory.partnership) {
          fireEvent(
            AnalyticEventTypes.subscription_offer_open,
            {
              screen_id: `landing_${screen}`,
              source: true,
            },
            false,
          );
        }
        pageOpenFired = true;
      }
    },
    [fireEvent, status],
  );

  const fireExperimentTrigger: ExperimentEvent = useCallback(
    (id, variant) => {
      fireEvent(AnalyticEventTypes.experiment_variant_trigger, {
        experimentVersion: `${id}.${variant}`,
      });
    },
    [fireEvent],
  );

  return {
    pageView,
    firePageOpen,
    isAnalyticsReady: isInitialized,
    fireEvent,
    pushDataLayer,
    fireExperimentTrigger,
  };
};

export default useAnalytics;
